import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge from '../components/EcfBadge';
import { DOC_TYPE_LABEL, type EcfEnvironment } from '../catalogos';
import { ecfService, type EcfSequence } from '../../../../services/ecf.service';

/**
 * Pestaña "Secuencias e-NCF": los rangos autorizados por la DGII para cada tipo de comprobante.
 *
 * Se muestran como lista y no como tabla porque cada rango necesita dos líneas (el rango en sí y
 * su estado de consumo), y una tabla con eso dentro se vuelve ilegible.
 */

interface SecuenciasTabProps {
  environment: EcfEnvironment;
}

const FORM_INICIAL = {
  docType: '32',
  rangeFrom: 1,
  rangeTo: 0,
  nextValue: 0,
  expiresAt: '',
  alertThreshold: 50,
};

export default function SecuenciasTab({ environment }: SecuenciasTabProps) {
  const [sequences, setSequences] = useState<EcfSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [form, setForm] = useState(FORM_INICIAL);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setSequences(await ecfService.listSequences());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar las secuencias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (row: EcfSequence) => {
    setBusyId(row.id);
    try {
      await ecfService.updateSequence(row.id, { active: !row.active });
      await load();
      toast.success(`Secuencia ${row.doc_type} ${row.active ? 'desactivada' : 'activada'}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo cambiar la secuencia');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row: EcfSequence) => {
    const usados = row.next_value - row.range_from;
    const aviso =
      usados > 0
        ? `Este rango ya tiene ${usados} secuencial(es) usados. Eliminarlo borra el control de por dónde iba: si vuelves a cargarlo, podrías repetir un e-NCF y la DGII lo rechazaría.\n\n¿Eliminar de todos modos?`
        : `¿Eliminar el rango del tipo ${row.doc_type}?`;
    if (!confirm(aviso)) return;

    setBusyId(row.id);
    try {
      await ecfService.deleteSequence(row.id);
      await load();
      toast.success('Secuencia eliminada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar la secuencia');
    } finally {
      setBusyId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ecfService.createSequence({
        docType: form.docType,
        rangeFrom: form.rangeFrom,
        rangeTo: form.rangeTo,
        nextValue: form.nextValue || undefined,
        expiresAt: form.expiresAt || null,
        alertThreshold: form.alertThreshold,
      });
      await load();
      setShowForm(false);
      setForm(FORM_INICIAL);
      toast.success('Secuencia guardada');
    } catch (err) {
      // El backend valida rango, próximo secuencial y la fecha obligatoria del tipo 31, y
      // devuelve el motivo concreto; se muestra tal cual.
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar la secuencia');
    } finally {
      setSaving(false);
    }
  };

  const yaExisteActiva = sequences.some((s) => s.active && s.doc_type === form.docType);

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            <i className="ri-list-ordered mr-2 text-blue-600"></i>
            Secuencias de e-NCF
          </h3>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500">
            Los rangos los autoriza la DGII por tipo de comprobante. El e-NCF se arma como la letra
            E, el tipo de dos dígitos y el secuencial de diez (por ejemplo E310000000001). Solo
            puede haber un rango activo por tipo.
          </p>

          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loading && loadError && (
            <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg flex items-center justify-between gap-4">
              <span>
                <i className="ri-error-warning-line mr-2"></i>
                {loadError}
              </span>
              <button onClick={load} className="text-sm font-medium hover:underline whitespace-nowrap">
                Reintentar
              </button>
            </div>
          )}

          {!loading && !loadError && (
            <>
              <div className="space-y-2">
                {sequences.map((s) => (
                  <div key={s.id} className="border border-gray-200 rounded-lg px-4 py-3">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">{s.doc_type}</span>
                          <span className="text-sm text-gray-700">
                            {DOC_TYPE_LABEL[s.doc_type]}
                          </span>
                          <EcfBadge variant={s.active ? 'success' : 'muted'}>
                            {s.active ? 'Activa' : 'Inactiva'}
                          </EcfBadge>
                          {s.expired && <EcfBadge variant="danger">Vencida</EcfBadge>}
                          {s.remaining === 0 && <EcfBadge variant="danger">Agotada</EcfBadge>}
                          {s.low && s.remaining > 0 && (
                            <EcfBadge variant="warning">Por agotarse</EcfBadge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Rango {s.range_from}–{s.range_to} · próximo {s.next_value} · quedan{' '}
                          {s.remaining}
                          {s.expires_at && ` · vence ${s.expires_at}`}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {busyId === s.id ? (
                          <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                        ) : (
                          <>
                            <button
                              onClick={() => toggleActive(s)}
                              className="text-sm text-blue-600 hover:text-blue-900 whitespace-nowrap"
                            >
                              {s.active ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              onClick={() => remove(s)}
                              className="text-red-600 hover:text-red-900"
                              title="Eliminar rango"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {sequences.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">
                    No hay secuencias e-CF cargadas. Agrega la primera para empezar a emitir.
                  </p>
                )}
              </div>

              {/* Formulario inline */}
              {!showForm ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Agregar secuencia
                </button>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de e-CF
                      </label>
                      <select
                        value={form.docType}
                        onChange={(e) => setForm((f) => ({ ...f, docType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        {Object.entries(DOC_TYPE_LABEL).map(([code, label]) => (
                          <option key={code} value={code}>
                            {code} — {label}
                          </option>
                        ))}
                      </select>
                      {yaExisteActiva && (
                        <p className="text-xs text-amber-700 mt-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          Ya hay un rango activo de este tipo. Al guardar, el anterior se
                          desactivará.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vencimiento{' '}
                        {form.docType === '31' ? (
                          <span className="text-red-600">(obligatorio)</span>
                        ) : (
                          <span className="text-gray-400">(opcional)</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={form.expiresAt}
                        onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {environment === 'test' && (
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, expiresAt: '2028-12-31' }))}
                          className="text-xs text-blue-600 hover:text-blue-900 mt-1"
                        >
                          En pruebas la DGII usa 31/12/2028 — usar esa fecha
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Desde (secuencial inicial)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.rangeFrom}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, rangeFrom: Number(e.target.value) }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hasta (secuencial final)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.rangeTo || ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, rangeTo: Number(e.target.value) }))
                        }
                        placeholder="1000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Próximo a usar (opcional)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.nextValue || ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, nextValue: Number(e.target.value) }))
                        }
                        placeholder="= inicial"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Avisar cuando queden
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={form.alertThreshold}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, alertThreshold: Number(e.target.value) }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setForm(FORM_INICIAL);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {saving ? 'Guardando...' : 'Guardar secuencia'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
